import {
    Badge,
    Box,
    Flex,
    Icon,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Tag,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdCalendarToday } from 'react-icons/md';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { headingFont } from 'theme/tokens';
import { translate } from '../../../i18n';
import {
    RoadmapItemDto,
    RoadmapStatus,
} from '../../../models/roadmap/dto/roadmap.dto';

// Lifecycle order, left to right: asked → planned → building → shipped.
const COLUMN_ORDER: RoadmapStatus[] = [
    'requested',
    'planned',
    'in_progress',
    'done',
];

// Warm Clubhouse semantic palette (DESIGN.md), not Chakra's default schemes:
// requested → draft gray, planned → slate info, in_progress → amber warning,
// done → emerald success. Backgrounds are the semantic color at ~12% opacity.
const STATUS_BADGE_LIGHT: Record<RoadmapStatus, { bg: string; color: string }> = {
    requested: { color: '#8A8680', bg: 'rgba(138,134,128,0.12)' },
    planned: { color: '#5B7B9A', bg: 'rgba(91,123,154,0.12)' },
    in_progress: { color: '#C4883A', bg: 'rgba(196,136,58,0.12)' },
    done: { color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
};

const STATUS_BADGE_DARK: Record<RoadmapStatus, { bg: string; color: string }> = {
    requested: { color: '#A8A49C', bg: 'rgba(168,164,156,0.14)' },
    planned: { color: '#7E9DBC', bg: 'rgba(91,123,154,0.20)' },
    in_progress: { color: '#D9A45C', bg: 'rgba(196,136,58,0.20)' },
    done: { color: '#34D399', bg: 'rgba(16,185,129,0.18)' },
};

function StatusBadge({ status }: { status: RoadmapStatus }) {
    const styles = useColorModeValue(STATUS_BADGE_LIGHT, STATUS_BADGE_DARK);
    const { bg, color } = styles[status];
    return (
        <Badge
            bg={bg}
            color={color}
            borderRadius="full"
            px="10px"
            py="3px"
            fontSize="11px"
            fontFamily={headingFont}
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="0.06em"
            lineHeight="1.4"
        >
            {statusLabel(status)}
        </Badge>
    );
}

function statusLabel(status: RoadmapStatus): string {
    switch (status) {
        case 'in_progress':
            return translate('roadmap.status_in_progress');
        case 'planned':
            return translate('roadmap.status_planned');
        case 'requested':
            return translate('roadmap.status_requested');
        case 'done':
            return translate('roadmap.status_done');
        default: {
            // Exhaustiveness guard: a new RoadmapStatus must be handled here.
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

function DueDate({ date, color }: { date: string; color: string }) {
    return (
        <Flex align="center" gap={1}>
            <Icon as={MdCalendarToday} boxSize="11px" color={color} />
            <Text color={color} fontSize="xs">
                {/* dueDate is a date-only ISO string ("2026-05-17"); format in UTC
                    so viewers west of UTC don't see it shifted a day earlier. */}
                {new Date(date).toLocaleDateString('fr-FR', { timeZone: 'UTC' })}
            </Text>
        </Flex>
    );
}

export default function RoadmapBoard({ items }: { items: RoadmapItemDto[] }) {
    const cardBg = useColorModeValue('white', 'navy.700');
    const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
    const hoverBorder = useColorModeValue('brand.500', 'brand.400');
    const titleColor = useColorModeValue('secondaryGray.900', 'white');
    const mutedColor = useColorModeValue('secondaryGray.500', 'secondaryGray.600');

    const [selected, setSelected] = useState<RoadmapItemDto | null>(null);

    const byStatus = (status: RoadmapStatus) =>
        items.filter((item) => item.status === status);

    return (
        <>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="16px">
                {COLUMN_ORDER.map((status) => {
                    const columnItems = byStatus(status);
                    const isEmpty = columnItems.length === 0;
                    return (
                        <Box key={status}>
                            <Flex
                                align="center"
                                gap={2}
                                mb="12px"
                                opacity={isEmpty ? 0.55 : 1}
                            >
                                <StatusBadge status={status} />
                                <Text color={mutedColor} fontSize="xs" fontWeight="600">
                                    {columnItems.length}
                                </Text>
                            </Flex>

                            {isEmpty ? (
                                <Flex
                                    border="1px dashed"
                                    borderColor={cardBorder}
                                    borderRadius="12px"
                                    py="20px"
                                    px="14px"
                                    justify="center"
                                    align="center"
                                >
                                    <Text color={mutedColor} fontSize="xs">
                                        {translate('roadmap.column_empty')}
                                    </Text>
                                </Flex>
                            ) : (
                            <Flex direction="column" gap="8px">
                                {columnItems.map((item) => {
                                    const hasDetails =
                                        Boolean(item.description) ||
                                        item.images.length > 0;
                                    return (
                                        <Box
                                            key={item.id}
                                            bg={cardBg}
                                            border="1px solid"
                                            borderColor={cardBorder}
                                            borderRadius="12px"
                                            px="14px"
                                            py="12px"
                                            {...(hasDetails
                                                ? {
                                                      cursor: 'pointer',
                                                      transition: 'border-color 0.15s',
                                                      _hover: { borderColor: hoverBorder },
                                                      onClick: () => setSelected(item),
                                                  }
                                                : {})}
                                        >
                                            <Text
                                                color={titleColor}
                                                fontSize="sm"
                                                fontWeight="600"
                                            >
                                                {item.title}
                                            </Text>
                                            {item.labels.length > 0 && (
                                                <Flex gap={1} mt="6px" wrap="wrap">
                                                    {item.labels.map((label) => (
                                                        <Tag
                                                            key={label}
                                                            size="sm"
                                                            variant="subtle"
                                                            colorScheme="gray"
                                                            borderRadius="full"
                                                            fontSize="11px"
                                                        >
                                                            {label}
                                                        </Tag>
                                                    ))}
                                                </Flex>
                                            )}
                                            {(item.dueDate || hasDetails) && (
                                                <Flex
                                                    align="center"
                                                    gap={3}
                                                    mt="6px"
                                                    wrap="wrap"
                                                >
                                                    {item.dueDate && (
                                                        <DueDate
                                                            date={item.dueDate}
                                                            color={mutedColor}
                                                        />
                                                    )}
                                                    {hasDetails && (
                                                        <Text
                                                            color="brand.500"
                                                            fontSize="xs"
                                                            fontWeight="600"
                                                        >
                                                            {translate('roadmap.view_details')}
                                                        </Text>
                                                    )}
                                                </Flex>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Flex>
                            )}
                        </Box>
                    );
                })}
            </SimpleGrid>

            <Modal
                isOpen={!!selected}
                onClose={() => setSelected(null)}
                size="xl"
                isCentered
                scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader pr="40px">{selected?.title}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb="24px">
                        <Flex align="center" gap={3} mb="16px" wrap="wrap">
                            {selected && <StatusBadge status={selected.status} />}
                            {selected?.dueDate && (
                                <DueDate date={selected.dueDate} color={mutedColor} />
                            )}
                            {selected?.labels.map((label) => (
                                <Tag
                                    key={label}
                                    size="sm"
                                    variant="subtle"
                                    colorScheme="gray"
                                    borderRadius="full"
                                >
                                    {label}
                                </Tag>
                            ))}
                        </Flex>

                        {selected?.description && (
                            <Box
                                fontSize="sm"
                                color={titleColor}
                                sx={{
                                    p: { mb: 3, lineHeight: 1.7 },
                                    'h1, h2, h3': {
                                        fontWeight: 'semibold',
                                        mt: 4,
                                        mb: 2,
                                    },
                                    'ul, ol': { pl: 5, mb: 3 },
                                    li: { mb: 1 },
                                    img: { maxW: '100%', borderRadius: 'md', my: 3 },
                                    a: { color: 'brand.500', textDecoration: 'underline' },
                                    code: {
                                        bg: 'gray.100',
                                        px: 1,
                                        borderRadius: 'sm',
                                        fontSize: 'xs',
                                    },
                                    blockquote: {
                                        borderLeft: '3px solid',
                                        borderColor: 'gray.300',
                                        pl: 3,
                                        my: 3,
                                        color: mutedColor,
                                    },
                                }}
                            >
                                <ReactMarkdown
                                    urlTransform={(url) =>
                                        /^(https?:|data:image\/)/i.test(url)
                                            ? url
                                            : ''
                                    }
                                >
                                    {selected.description}
                                </ReactMarkdown>
                            </Box>
                        )}

                        {selected && selected.images.length > 0 && (
                            <Flex
                                direction="column"
                                gap={3}
                                mt={selected.description ? 4 : 0}
                            >
                                {selected.images.map((src, index) => (
                                    <Image
                                        key={index}
                                        src={src}
                                        alt=""
                                        borderRadius="md"
                                        maxW="100%"
                                    />
                                ))}
                            </Flex>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}
